import { Trans } from "@lingui/macro";
import { UiAlert, UiBox, UiTypography } from "@smp/ui";
import { numberBitGetByPosition, textCurrencySymbolGet } from "@smp/utils";
import parseISO from "date-fns/parseISO";
import { FC, useMemo } from "react";

import { useEpBillingRecurringPaymentList } from "@/hooks/use_ep_billing_recurring_payment";
import { useHumanizer } from "@/hooks/use_humanizer";
import {
  useQueryBillingPlanProductSet,
  useQueryBillingPlansubscriptionSet,
} from "@/queries/billing";
import { useContextApp } from "@/screens/app/context/app";

import { IInvoiceFormValues } from "../../use_invoice_form";

export interface IWarningsProps {
  paymentType: IInvoiceFormValues["paymentType"];
  product: IInvoiceFormValues["product"];
}

export const Warnings: FC<IWarningsProps> = ({ paymentType, product }) => {
  const isProductPlan = product.type === "plan-product";
  const trialPlanId = isProductPlan
    ? product.plan?.trial || undefined
    : undefined;

  const {
    state: { me },
  } = useContextApp();

  const { humanizeBillingPlanId, humanizePeriodUnit } = useHumanizer();

  const { data: planSubscriptionList } = useQueryBillingPlansubscriptionSet({
    ordering: "-period_end",
  });

  const { recurringPaymentList } = useEpBillingRecurringPaymentList({
    req: { query: { ordering: "-created_at" } },
  });

  const { data: productPlanTrialList } = useQueryBillingPlanProductSet(
    {
      billing_policy_id: me.billing_policy_id,
      plan_id: trialPlanId,
    },
    { enabled: !!trialPlanId },
  );

  const productPlanTrial = productPlanTrialList?.at(0);

  const planSubscriptionLast = planSubscriptionList?.results.at(0);

  const planSubscriptionActive = planSubscriptionLast
    ? parseISO(planSubscriptionLast.period_end).getTime() >=
      new Date().getTime()
      ? planSubscriptionLast
      : undefined
    : undefined;

  const recurringPaymentActive = useMemo(
    () =>
      recurringPaymentList?.find(
        (recurringPayment) =>
          !!numberBitGetByPosition(recurringPayment.status, 1),
      ),
    [recurringPaymentList],
  );

  const isWarningPaidPeriodVisible =
    paymentType === "recurring" &&
    !recurringPaymentActive &&
    !!planSubscriptionActive;

  const isWarningRecurringPaymentTrialVisible = !!productPlanTrial;

  const isWarningRecurringPaymentActiveProductSameVisible =
    paymentType === "recurring" &&
    recurringPaymentActive &&
    recurringPaymentActive.product_regular === product.id;

  const isWarningRecurringPaymentActiveProductNotSameVisible =
    paymentType === "recurring" &&
    recurringPaymentActive &&
    recurringPaymentActive.product_regular !== product.id;

  const isAnyWarningVisible =
    isWarningPaidPeriodVisible ||
    isWarningRecurringPaymentActiveProductNotSameVisible ||
    isWarningRecurringPaymentActiveProductSameVisible ||
    isWarningRecurringPaymentTrialVisible;

  if (product.type !== "plan-product") {
    return null;
  }

  if (!isAnyWarningVisible) {
    return null;
  }

  return (
    <>
      {isWarningPaidPeriodVisible && (
        <UiBox mb={2}>
          <UiAlert variant="warning">
            <Trans>
              If after your PRO Plan expires you still have paid days, they will
              be saved, and used when you activate a new subscription. The
              remainder will be used only after the expiration of the
              subscription. You can check available days in the Subscription
              settings.
            </Trans>
          </UiAlert>
        </UiBox>
      )}
      {isWarningRecurringPaymentActiveProductNotSameVisible && (
        <UiBox mb={2}>
          <UiAlert variant="warning">
            <Trans>
              If you change your Subscription Plan, paid days will be
              recalculated and converted into the new Subscription Plan. If the
              only change is the payment method, no conversion takes place. The
              remaining days will be used only after the expiration of the paid
              subscription.
            </Trans>
          </UiAlert>
        </UiBox>
      )}
      {isWarningRecurringPaymentActiveProductSameVisible && (
        <UiBox mb={2}>
          <UiAlert variant="warning">
            <Trans>
              If you change your Subscription Plan, paid days will be
              recalculated and converted into the new Subscription Plan. If the
              only change is the payment method, no conversion takes place. The
              remaining days will be used only after the expiration of the paid
              subscription.
            </Trans>
          </UiAlert>
        </UiBox>
      )}
      {isWarningRecurringPaymentTrialVisible && (
        <UiBox mb={2}>
          <UiAlert variant="warning">
            <UiTypography>
              <Trans>
                {humanizeBillingPlanId(productPlanTrial.plan.id)} Subscription
                Plan will be activated for{" "}
                {humanizePeriodUnit(
                  productPlanTrial.period_units,
                  productPlanTrial.period_length,
                )}
                .
              </Trans>
            </UiTypography>
            <UiTypography>
              <Trans>
                After the trial period, the{" "}
                {humanizeBillingPlanId(product.plan.id)} Subscription Plan will
                be activated with a{" "}
                {humanizePeriodUnit(
                  product.period_units,
                  product.period_length,
                )}{" "}
                charge period, for {product.price}{" "}
                {textCurrencySymbolGet(product.currency)}.
              </Trans>
            </UiTypography>
            <UiTypography>
              <Trans>
                The setup payment will be charged when the trial period is
                activated (and will be refunded immediately).
              </Trans>
            </UiTypography>
          </UiAlert>
        </UiBox>
      )}
    </>
  );
};
